import {
  Button,
  Form,
  FormControl,
  FormField,
  FormSubmitHandler,
  showNotification,
  useDialog,
} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';

import {useBulkUpdateOpeningHoursMutation} from '@dms/api/base';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {CopyToOtherBranchesDialog} from './CopyToOtherBranchesDialog';
import {
  CategoryOpeningHoursFormValues,
  DAYS,
  DaySlots,
  OpeningHoursCategory,
  buildCategoryDefaultValues,
  normalizeDaySlotsToHHMMSS,
  CategoryOpeningHoursFormSchema,
} from './openingHoursUtils';

const DAY_LABEL_WIDTH = 30;
const INPUT_WIDTH = 35;

interface OpeningHoursGridProps {
  control: FormControl<CategoryOpeningHoursFormValues>;
  description: string;
  formApi: UseFormReturn<CategoryOpeningHoursFormValues>;
}

function OpeningHoursGrid(props: OpeningHoursGridProps) {
  return (
    <VStack spacing={5}>
      <Text color="secondary" size="small">
        {props.description}
      </Text>
      <VStack spacing={3}>
        <HStack spacing={4}>
          <Box width={DAY_LABEL_WIDTH} />
          <Box width={INPUT_WIDTH}>
            <Text size="small" alternative>
              {i18n.t('entity.branch.labels.from')}
            </Text>
          </Box>
          <Box width={INPUT_WIDTH}>
            <Text size="small" alternative>
              {i18n.t('entity.branch.labels.to')}
            </Text>
          </Box>
        </HStack>

        {DAYS.map((day) => (
          <HStack key={day} spacing={4} align="center">
            <Box width={DAY_LABEL_WIDTH}>
              <Text size="base">{i18n.t(`general.labels.calendar.${day}`)}</Text>
            </Box>

            <HStack key={day} spacing={4} align="flex-start">
              <Box width={INPUT_WIDTH}>
                <FormField
                  control={props.control}
                  type="time"
                  name={`openingHours.${day}.0.from`}
                  placeholder="00:00"
                  data-testid={testIds.settings.branches(`openingHours-${day}From`)}
                  onChange={() => {
                    if (props.formApi.formState.isSubmitted) {
                      props.formApi.trigger([
                        `openingHours.${day}.0.from`,
                        `openingHours.${day}.0.to`,
                      ]);
                    }
                  }}
                />
              </Box>

              <Box width={INPUT_WIDTH}>
                <FormField
                  control={props.control}
                  type="time"
                  name={`openingHours.${day}.0.to`}
                  placeholder="00:00"
                  data-testid={testIds.settings.branches(`openingHours-${day}To`)}
                  onChange={() => {
                    if (props.formApi.formState.isSubmitted) {
                      props.formApi.trigger([
                        `openingHours.${day}.0.from`,
                        `openingHours.${day}.0.to`,
                      ]);
                    }
                  }}
                />
              </Box>
            </HStack>
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
}

interface CategoryOpeningHoursFormProps {
  category: OpeningHoursCategory;
  branchId: string;
  description: string;
  branchOpeningHours: Partial<DaySlots> | null | undefined;
}

export function CategoryOpeningHoursForm(props: CategoryOpeningHoursFormProps) {
  const [bulkUpdateOpeningHours] = useBulkUpdateOpeningHoursMutation();
  const [isDialogOpen, onDialogOpen, onDialogClose, {key: dialogKey}] = useDialog();

  const defaultValues = buildCategoryDefaultValues(props.branchId, props.branchOpeningHours);

  const handleSubmit: FormSubmitHandler<CategoryOpeningHoursFormValues> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    await bulkUpdateOpeningHours({
      branchIds: values.branchIds,
      [props.category]: values.openingHours ? normalizeDaySlotsToHHMMSS(values.openingHours) : null,
    })
      .unwrap()
      .then(() => {
        showNotification.success();
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <Form<CategoryOpeningHoursFormValues>
      defaultValues={defaultValues}
      experimentalZodSchema={CategoryOpeningHoursFormSchema}
      shouldWatchForUnsavedChanges
      onSubmit={handleSubmit}
    >
      {(control, formApi, navigateWithoutBlocker) => (
        <VStack spacing={6}>
          <SettingsSection>
            <OpeningHoursGrid control={control} description={props.description} formApi={formApi} />

            <Box paddingTop={5}>
              <Button
                variant="link"
                title={i18n.t('entity.branch.labels.copyToOtherBranches')}
                leftIcon="content/copy"
                onClick={onDialogOpen}
                data-testid={testIds.settings.branches('openingHoursCopyToOther')}
              />
            </Box>
          </SettingsSection>

          <CopyToOtherBranchesDialog
            key={dialogKey}
            isOpen={isDialogOpen}
            onClose={onDialogClose}
            onConfirmSuccess={() => formApi.reset(undefined, {keepValues: true})}
            currentBranchId={props.branchId}
            activeCategory={props.category}
            openingHoursData={formApi.getValues('openingHours') ?? {}}
          />

          <SettingsFooter
            actions={[
              {
                type: 'button',
                title: i18n.t('general.actions.discardChanges'),
                variant: 'secondary',
                onClick: () => navigateWithoutBlocker(settingsRoutes.branches),
                'data-testid': testIds.settings.branches('openingHoursDiscard'),
              },
              {
                type: 'form-button',
                control,
                title: i18n.t('general.actions.saveAndClose'),
                buttonType: 'submit',
                variant: 'primary',
                'data-testid': testIds.settings.branches('openingHoursSave'),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
